import i18n from "@/libraries/i18n";
import { z } from "zod";

/**
 * Global fallback for Zod issues that don't carry an explicit `message`. Zod
 * gives an explicit per-call message precedence over this map, so this only
 * covers schemas that rely on the built-in wording (`z.string()`, bare
 * `.min()`/`.max()` with no message argument) - it does NOT retroactively
 * translate the many schemas in src/schemas/ that already pass a literal
 * English string, since Zod would still prefer that literal. Those need to be
 * converted to factories (see createAuthLoginSchema in schemas/auth.ts for the
 * pattern) as they're migrated.
 */
const errorMap: z.ZodErrorMap = (issue, ctx) => {
	const t = i18n.t.bind(i18n);

	switch (issue.code) {
		case z.ZodIssueCode.invalid_type:
			if (issue.received === "undefined") {
				return { message: t("validation:required") };
			}
			return { message: t("validation:invalidType") };
		case z.ZodIssueCode.invalid_string:
			if (issue.validation === "email")
				return { message: t("validation:invalidEmail") };
			if (issue.validation === "url")
				return { message: t("validation:invalidUrl") };
			return { message: t("validation:invalid") };
		case z.ZodIssueCode.too_small:
			if (issue.type === "string") {
				return { message: t("validation:tooShort", { min: issue.minimum }) };
			}
			return { message: t("validation:tooSmall", { min: issue.minimum }) };
		case z.ZodIssueCode.too_big:
			if (issue.type === "string") {
				return { message: t("validation:tooLong", { max: issue.maximum }) };
			}
			return { message: t("validation:tooBig", { max: issue.maximum }) };
		default:
			return { message: ctx.defaultError };
	}
};

export function initZodI18n() {
	z.setErrorMap(errorMap);
}
